import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../../config/supabase';
import {
    EMAIL_SIGNATURE_MAX, TEXT_SIGNATURE_MAX, assembleSignature, companySettingsFromRow, isGsm7, loadCompanySignatureSettings,
    loadEmailCompanyBlock, loadMySignature, resolveActiveSignatureCompany, saveActiveSignatureCompany, saveMySignature, signatureErrorMessage,
} from '../../../lib/signatures';
import { useSaveToast } from '../../ui/SaveToast';
import HelpButton from '../../ui/HelpButton';
import { EmailCompanyBlock, TextSignatureProblem } from './SignatureParts';
import { D2_LABEL, D2_TEXTAREA } from './d2Fields';

// BL-177 Step 2d (section 10): Settings › Communications › My signature. Everyone writes only their own part and
// sees the finished result (their part, then the company's) as they type. Each box saves when you leave it.

const FAILED_PREFIX = "Couldn't save. Switched back.";
const SAMPLE = 'Your message here...';

function LockIcon() {
    return (
        <svg aria-hidden="true" viewBox="0 0 16 16" width="14" height="14" className="flex-none">
            <rect x="3" y="7" width="10" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    );
}

export default function MySignatureView({
    orgId,
    userId,
    brandEntityId = '',
    headerBrandEntityId = '',
    platformDoorContext = null,
    client = supabaseClient,
}) {
    const toast = useSaveToast();
    const [company, setCompany] = useState(null);
    const [block, setBlock] = useState(null);
    const [brandOptions, setBrandOptions] = useState([]);
    const [showCompanyPicker, setShowCompanyPicker] = useState(false);
    const [selectedBrandEntityId, setSelectedBrandEntityId] = useState('');
    const [draft, setDraft] = useState({ text_signature: '', email_signature: '' });
    const [error, setError] = useState('');
    const saved = useRef({ text_signature: '', email_signature: '' });

    const load = useCallback(async () => {
        try {
            const [companyRow, mine, companyContext] = await Promise.all([
                loadCompanySignatureSettings(client, orgId),
                loadMySignature(client, orgId, userId),
                resolveActiveSignatureCompany(client, {
                    orgId,
                    userId,
                    doorEntityId: platformDoorContext?.entity_id || '',
                    headerEntityId: headerBrandEntityId || brandEntityId,
                }),
            ]);
            const next = { text_signature: mine?.text_signature || '', email_signature: mine?.email_signature || '' };
            saved.current = next;
            setDraft(next);
            setCompany(companySettingsFromRow(companyRow));
            setBrandOptions(companyContext.options);
            setShowCompanyPicker(companyContext.showPicker);
            setSelectedBrandEntityId(companyContext.selectedId);
            setError('');
        } catch (loadError) {
            setError(loadError.message || 'Could not load your signature.');
        }
    }, [brandEntityId, client, headerBrandEntityId, orgId, platformDoorContext?.entity_id, userId]);
    useEffect(() => { if (orgId && userId) load(); }, [orgId, userId, load]);
    useEffect(() => {
        let current = true;
        if (!selectedBrandEntityId) {
            setBlock(null);
            return () => { current = false; };
        }
        loadEmailCompanyBlock(client, orgId, selectedBrandEntityId)
            .then((nextBlock) => { if (current) setBlock(nextBlock); })
            .catch((loadError) => { if (current) setError(loadError.message || 'Could not load the company email block.'); });
        return () => { current = false; };
    }, [client, orgId, selectedBrandEntityId]);

    async function save(field, value) {
        const text = String(value).replace(/\r\n?/g, '\n');
        if (text === saved.current[field]) return;
        if (field === 'text_signature' && !isGsm7(text)) return;
        const before = saved.current;
        const next = { ...before, [field]: text };
        setDraft((current) => ({ ...current, [field]: text }));
        try {
            await saveMySignature(client, orgId, userId, next);
            saved.current = next;
            toast.saved();
        } catch (saveError) {
            setDraft((current) => ({ ...current, [field]: before[field] }));
            toast.failed(`${FAILED_PREFIX} ${signatureErrorMessage(saveError)}`);
        }
    }

    const mode = company?.personal_signature_mode || 'optional';
    const personalOff = mode === 'off';
    const companySettings = company ? { ...company, email_block: block } : null;
    const selectedBrand = brandOptions.find((option) => option.id === selectedBrandEntityId);
    const textSig = assembleSignature({ channel: 'sms', companySettings, personalSignature: draft });
    const emailSig = assembleSignature({ channel: 'email', companySettings, personalSignature: draft });
    const emailLines = emailSig.personalPart ? emailSig.personalPart.split('\n') : [];

    return (
        <div className="ia-d2 max-w-full space-y-4 rounded-2xl p-3 sm:p-5" aria-label="My signature">
            {showCompanyPicker && (
                <div className="max-w-3xl">
                    <label htmlFor="my-signature-company" className={D2_LABEL}>Which company?</label>
                    <select id="my-signature-company" className={`${D2_TEXTAREA} mt-2`}
                        value={selectedBrandEntityId} onChange={(event) => {
                            const nextId = event.target.value;
                            setSelectedBrandEntityId(nextId);
                            try {
                                saveActiveSignatureCompany(undefined, orgId, userId, nextId);
                                setError('');
                            } catch (saveError) {
                                setError(saveError.message || 'Could not remember your company choice in this browser.');
                            }
                        }}>
                        <option value="">Choose a company or brand</option>
                        {brandOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                    </select>
                </div>
            )}
            <p className="text-sm">
                Two parts, always in the same order: your signature, then the company&apos;s. The company signature is set by your company.
            </p>
            {error && <p role="alert" className="rounded-lg border border-red-500/60 bg-red-900/40 px-3 py-2 text-sm">{error}</p>}
            {company && (
                <section className="ia-d2-card max-w-3xl space-y-5">
                    <div>
                        <label htmlFor="my-text-signature" className={`${D2_LABEL} flex items-center gap-2`}>
                            My signature for texts and WhatsApp <span className="font-normal">· multi-line</span>
                        </label>
                        {personalOff && <p className="mt-1 text-sm">Your company has personal signatures turned off, so only the company signature is added.</p>}
                        {mode === 'required' && <p className="mt-1 text-sm">Your company requires a personal signature on every signed message.</p>}
                        <textarea id="my-text-signature" rows={3} value={draft.text_signature} disabled={personalOff} maxLength={TEXT_SIGNATURE_MAX}
                            className={`${D2_TEXTAREA} mt-2`} placeholder="- Your name"
                            onChange={(event) => setDraft({ ...draft, text_signature: event.target.value })}
                            onBlur={(event) => save('text_signature', event.target.value)} />
                        <TextSignatureProblem text={draft.text_signature} onFix={(fixed) => save('text_signature', fixed)} />
                    </div>

                    <div>
                        <h2 className={D2_LABEL}>Company signature <span className="font-normal">· Set by your company</span></h2>
                        <div className="mt-2 flex items-center gap-3 rounded-xl border border-dashed border-slate-500 bg-[#0F172A] px-3 py-2.5 text-sm"
                            aria-label="Company signature, set by your company">
                            <LockIcon />
                            {company.always_on
                                ? <span className="whitespace-pre-line break-words">{company.text_signature || 'Not written yet'}</span>
                                : <span>Your company signature is off.</span>}
                        </div>
                    </div>

                    <div>
                        <h2 className={`${D2_LABEL} flex items-center gap-2`}>
                            How your texts will end
                            <HelpButton
                                title="How your texts will end"
                                what="A preview of the end of a text you write: your signature, then the company's."
                                who="Customers you text or WhatsApp from Communications."
                                when="On every eligible text and email you write. Never on WhatsApp templates or automatic notifications."
                            />
                        </h2>
                        <div data-testid="text-preview" className="mt-2 inline-block max-w-full whitespace-pre-line break-words rounded-2xl border border-[#2DD4BF]/60 bg-[#14524C] px-4 py-3 text-[15px] leading-relaxed text-white">
                            {`${SAMPLE}${textSig.text}`}
                        </div>
                        <p className="mt-2 text-sm" aria-live="polite">
                            Adds {textSig.addedChars} characters to each text (each line break counts as 1).
                            {' '}Plain characters only, so texts stay standard length.
                        </p>
                    </div>

                    <div>
                        <label htmlFor="my-email-signature" className={D2_LABEL}>My email lines</label>
                        <textarea id="my-email-signature" rows={3} value={draft.email_signature} disabled={personalOff} maxLength={EMAIL_SIGNATURE_MAX}
                            className={`${D2_TEXTAREA} mt-2`} placeholder={'Your name\nYour title'}
                            onChange={(event) => setDraft({ ...draft, email_signature: event.target.value })}
                            onBlur={(event) => save('email_signature', event.target.value)} />
                        <p className="mt-2 text-sm" data-testid="signature-company-context">
                            Company for this email block: {selectedBrand?.label || 'Choose a company'}
                        </p>
                        <div data-testid="email-preview" className="mt-3 rounded-2xl bg-white p-4 text-[#0B1220]">
                            {emailLines.length > 0 && (
                                <div className="mb-3 border-b border-slate-200 pb-3">
                                    <div className="text-lg font-bold">{emailLines[0]}</div>
                                    {emailLines.slice(1).map((line, index) => <div key={`${index}-${line}`} className="text-sm">{line}</div>)}
                                </div>
                            )}
                            {emailSig.emailCompanyBlock ? <EmailCompanyBlock block={emailSig.emailCompanyBlock} compact /> : (
                                <p className="text-sm">{company.always_on ? 'The company block appears once Brands & Locations has a company name.' : 'Your company signature is off.'}</p>
                            )}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
